import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Clock, CheckCircle, XCircle, SkipForward, Eye, MousePointerClick, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

interface EmailSequence {
  id: number;
  userId: number;
  sequenceType: string;
  variant: string;
  step: number;
  scheduledFor: Date;
  sentAt: Date | null;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  openedAt: Date | null;
  clickedAt: Date | null;
  trackingToken: string | null;
  metadata: any;
  createdAt: Date;
}

export default function AdminEmailSequences() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sequenceTypeFilter, setSequenceTypeFilter] = useState<string>('all');

  const { data: sequences, isLoading } = useQuery<{ sequences: EmailSequence[] }>({
    queryKey: ['/api/admin/email-sequences'],
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'skipped':
        return <SkipForward className="w-4 h-4 text-gray-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      sent: 'default',
      failed: 'destructive',
      skipped: 'secondary',
      pending: 'outline',
    };

    return (
      <Badge variant={variants[status] || 'default'} className="flex items-center gap-1 w-fit">
        {getStatusIcon(status)}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const calculateMetrics = () => {
    if (!sequences?.sequences) return { total: 0, sent: 0, opened: 0, clicked: 0, openRate: 0, clickRate: 0 };

    const total = sequences.sequences.length;
    const sent = sequences.sequences.filter(s => s.status === 'sent').length;
    const opened = sequences.sequences.filter(s => s.openedAt).length;
    const clicked = sequences.sequences.filter(s => s.clickedAt).length;

    return {
      total,
      sent,
      opened,
      clicked,
      openRate: sent > 0 ? ((opened / sent) * 100).toFixed(1) : '0',
      clickRate: opened > 0 ? ((clicked / opened) * 100).toFixed(1) : '0',
    };
  };

  const metrics = calculateMetrics();

  const filteredSequences = sequences?.sequences.filter((seq) => {
    if (statusFilter !== 'all' && seq.status !== statusFilter) return false;
    if (sequenceTypeFilter !== 'all' && seq.sequenceType !== sequenceTypeFilter) return false;
    return true;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading email sequences...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Email Sequence Tracking</h1>
        <p className="text-muted-foreground">Monitor email sequence performance and engagement</p>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Sequences</CardDescription>
            <CardTitle className="text-2xl">{metrics.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Sent</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              {metrics.sent}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Opened</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-500" />
              {metrics.opened}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Clicked</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <MousePointerClick className="w-5 h-5 text-purple-500" />
              {metrics.clicked}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Open Rate</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-500" />
              {metrics.openRate}%
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Click Rate</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              {metrics.clickRate}%
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sequence Type</Label>
              <Select value={sequenceTypeFilter} onValueChange={setSequenceTypeFilter}>
                <SelectTrigger data-testid="filter-sequence-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="welcome">Welcome</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="engagement">Engagement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>User ID</Label>
              <Input placeholder="Filter by user ID" type="number" data-testid="filter-user-id" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sequences Table */}
      <Card>
        <CardHeader>
          <CardTitle>Email Sequences</CardTitle>
          <CardDescription>
            Showing {filteredSequences.length} of {sequences?.sequences.length || 0} sequences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Step</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead>Clicked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSequences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    No email sequences found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSequences.map((sequence) => (
                  <TableRow key={sequence.id} data-testid={`row-sequence-${sequence.id}`}>
                    <TableCell>{sequence.id}</TableCell>
                    <TableCell>{sequence.userId}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{sequence.sequenceType}</Badge>
                    </TableCell>
                    <TableCell>{sequence.step}</TableCell>
                    <TableCell>
                      <Badge>{sequence.variant}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(sequence.status)}</TableCell>
                    <TableCell className="text-sm">
                      {format(new Date(sequence.scheduledFor), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="text-sm">
                      {sequence.sentAt ? format(new Date(sequence.sentAt), 'MMM d, HH:mm') : '-'}
                    </TableCell>
                    <TableCell>
                      {sequence.openedAt ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <Eye className="w-3 h-3" />
                          <span className="text-sm">
                            {format(new Date(sequence.openedAt), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {sequence.clickedAt ? (
                        <div className="flex items-center gap-1 text-purple-600">
                          <MousePointerClick className="w-3 h-3" />
                          <span className="text-sm">
                            {format(new Date(sequence.clickedAt), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
